/**
 * Entry for the React app. Initializes Sentry only when a DSN is provided
 * (e.g., injected via index.html as `window.__SENTRY_DSN__`). This avoids
 * bundling or evaluating Sentry during local development when no DSN is set.
 */

import { createRoot, hydrateRoot } from "react-dom/client";
import "./index.css";
import { App } from "./App";
import { ErrorBoundary } from "./components/ErrorBoundary";

// Safely suppress benign browser AbortErrors (such as skipped View Transitions)
if (typeof window !== "undefined") {
  window.addEventListener("unhandledrejection", (event) => {
    if (
      event.reason?.name === "AbortError" ||
      event.reason?.message?.includes?.("Transition was skipped")
    ) {
      event.preventDefault();
    }
  });
}

function start() {
  const container = document.getElementById("root")!;
  const appElement = (
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  );

  if (container.hasChildNodes()) {
    try {
      hydrateRoot(container, appElement);
      return;
    } catch (e) {
      console.warn("Hydration fallback to client render:", e);
    }
  }

  createRoot(container).render(appElement);
}

async function bootstrap() {
  try {
    const resp = await fetch("/__sentry_dsn");
    if (!resp.ok) {
      // Nothing to do if endpoint not OK (204 or 404 from proxies)
      console.debug("/__sentry_dsn returned non-ok status", resp.status);
    } else {
      const contentType = resp.headers.get("content-type") || "";
      let payload: any = null;
      if (resp.status === 204) {
        // No content
        payload = null;
      } else if (contentType.includes("application/json")) {
        try {
          payload = await resp.json();
        } catch (err) {
          console.warn("/__sentry_dsn: failed to parse JSON", err);
          payload = null;
        }
      } else {
        // If backend returned HTML or other, avoid parsing and log for diagnostics
        const text = await resp.text();
        console.warn(
          "/__sentry_dsn: unexpected content-type",
          contentType,
          text.slice(0, 200),
        );
        payload = null;
      }

      const ds = payload?.dsn as string | undefined;
      if (ds) {
        try {
          const Sentry = await import("@sentry/react");
          Sentry.init({
            dsn: ds,
            integrations: [
              Sentry.browserTracingIntegration(),
              Sentry.replayIntegration(),
            ],
            tracesSampleRate: 1.0,
            replaysSessionSampleRate: 0.1,
            replaysOnErrorSampleRate: 1.0,
          });
          console.log("Sentry initialized (browser)");
        } catch (err) {
          console.error("Sentry (browser) init failed:", err);
        }
      }
    }
  } catch (err) {
    console.error("Failed to fetch /__sentry_dsn:", err);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
}

bootstrap();
